
import "../Offers/OffersListing/CreateOffer.css";
import React, { useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { failed, success } from "@components/Common/Toastify";
import { createOffer } from "../../API/Offers";
import storage from "@utils/storage";
import { useSocket } from "@utils/socket/socketContext";
import axios from "axios";
import envConfig from "../../environment";
import { Modal } from "bootstrap";


export const CreateOffer = () => {
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const Id = storage.getUser();
  const isProfileComplete = storage.getUser();
  const userId = Id?.user?.userId;
  const { socket } = useSocket();

  const formik = useFormik({
    initialValues: {
      lenderId: userId,
      title: "",
      loanAmount: "",
      interestRate: "",
      loanDuration: "",
      termsAndConditions: "",
    },
    validationSchema: yup.object({
      lenderId: yup.string().required("Lender ID is required"),
      title: yup.string().required("Title is required"),
      loanAmount: yup
        .number()
        .required("Loan amount is required")
        .min(10, "Minimum loan amount is 10"),
      interestRate: yup
        .number()
        .required("Interest rate is required")
        .min(0.01, "Interest rate must be greater than 0"),
      loanDuration: yup
        .number()
        .required("Loan duration is required")
        .min(1, "Minimum duration is 1 month"),
      termsAndConditions: yup
        .string()
        .required("Terms and conditions are required"),
    }),
    onSubmit: (values) => {
      setBtnLoading(true);

      createOffer(values)
        .then(async (res: any) => {
          success(res?.data?.message || "Offer created successfully!");

          const notificationMessage = `New offer with amount £${values.loanAmount} at ${values.interestRate}% for ${values.loanDuration} months.`;
          const notificationTitle = `New Offer : ${values.title}`;

          // First, save the notification to DB via API
          try {
            await axios.post(
              `${envConfig.apiUrl}/notification/create-offer-notification`,
              {
                senderId: userId,
                senderType: "lender",
                receiverType: "borrower",
                title: notificationTitle,
                notification: notificationMessage,
                isGlobal: true,
              },
              {
                headers: {
                  Authorization: `Bearer ${storage.getToken()}`,
                },
              }
            );
            if (socket?.connected) {
              socket.emit("offer created", notificationMessage);
            }

          } catch (apiErr) {
            console.error("Failed to save notification:", apiErr);
          }

          navigate("/app/open-offers");
        })
        .catch((err) => {
          failed(err?.response?.data?.message || "Offer creation failed.");
        })
        .finally(() => setBtnLoading(false));
    },
  });

  return (
    <div className="content create-offer-section">
      <div className="content-wrapper">
        <h5 className="heading m-0">Create Offer</h5>
        <div className="outer-box-wrapper">
          <form className="lender-form" onSubmit={formik.handleSubmit}>
            <div className="row mb-4">
              <div className="col-lg-6 email-name-label">
                <label htmlFor="title" className="form-label">
                  Title <span className="asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="title"
                  placeholder="Title"
                  {...formik.getFieldProps("title")}
                />
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger">{formik.errors.title}</div>
                )}
              </div>
              <div className="col-lg-6">
                <label htmlFor="loanAmount" className="form-label">
                  Loan Amount <span className="asterisk">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="loanAmount"
                  placeholder="Loan amount"
                  {...formik.getFieldProps("loanAmount")}
                />
                {formik.touched.loanAmount && formik.errors.loanAmount && (
                  <div className="text-danger">{formik.errors.loanAmount}</div>
                )}
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-lg-6 email-name-label">
                <label htmlFor="loanDuration" className="form-label">
                  Loan Duration (months) <span className="asterisk">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="loanDuration"
                  placeholder="Loan duration"
                  {...formik.getFieldProps("loanDuration")}
                />
                {formik.touched.loanDuration && formik.errors.loanDuration && (
                  <div className="text-danger">
                    {formik.errors.loanDuration}
                  </div>
                )}
              </div>
              <div className="col-lg-6">
                <label htmlFor="interestRate" className="form-label">
                  Interest Rate (%) <span className="asterisk">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  id="interestRate"
                  placeholder="Interest rate"
                  value={formik.values.interestRate}
                  onChange={(e) => {
                    let value = parseFloat(e.target.value);

                    // If the input is not a number or empty, pass it as-is
                    if (isNaN(value)) {
                      formik.setFieldValue("interestRate", e.target.value);
                    } else {
                      // Round up values less than 1 to 1
                      if (value < 0) value = 0;
                      formik.setFieldValue("interestRate", value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                />

                {formik.touched.interestRate && formik.errors.interestRate && (
                  <div className="text-danger">
                    {formik.errors.interestRate}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="termsAndConditions" className="form-label">
                Terms & Conditions <span className="asterisk">*</span>
              </label>
              <textarea
                className="form-control"
                id="termsAndConditions"
                rows={4}
                placeholder="Enter terms and conditions"
                {...formik.getFieldProps("termsAndConditions")}
              />
              {formik.touched.termsAndConditions &&
                formik.errors.termsAndConditions && (
                  <div className="text-danger">
                    {formik.errors.termsAndConditions}
                  </div>
                )}
            </div>

            <div className="create-offr-btn text-center">
              <button
                type="button" // <-- Not "submit" to intercept click
                className="btn btn-lender create-new-offer "
                disabled={btnLoading}
                onClick={() => {
                  const isProfileCompleted = isProfileComplete?.user?.isProfileCompleted;

                  if (!isProfileCompleted) {
                    const modalElement = document.getElementById("signModal");
                    if (modalElement) {
                      const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
                      modalInstance.show();
                    }
                    return;
                  }

                  // Proceed to submit if profile is complete
                  formik.handleSubmit();
                }}
              >
                {btnLoading ? "Creating..." : "Create New Offer"}
              </button>

            </div>
          </form>
          <div className="modal fade" id="signModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered sidebar-modal-box">
              <div className="modal-content">
                <div className="delete-popup">
                  <div className="signature-section">

                    <div className="p-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#f8d7da" stroke="#842029" strokeWidth="2" />
                        <line x1="12" y1="7" x2="12" y2="12" stroke="#842029" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1.2" fill="#842029" />
                      </svg>
                      <p className="applicant-infofields mb-10">
                        Please complete your profile before creating the Offer.
                      </p>
                      <button
                        type="button"
                        className="send-contract-button"
                        onClick={() => {
                          const modalElement = document.getElementById('signModal');
                          navigate('/app/profile')
                          if (modalElement) {
                            const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
                            modalInstance.hide();
                          }
                        }}
                      >
                        Go to Profile Page
                      </button>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOffer;
